/**
 * Thin fetch wrapper: same-origin, sends the session cookie, and sets the
 * `X-Requested-With: fetch` header the API's CSRF check requires (spec 06).
 */
import type {
  MeResponse,
  CheckoutResponse,
  PaymentProvider,
  ShareCreateResponse,
  ShareGetResponse,
  SharePointIn,
} from '@boatnav/schemas';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      'x-requested-with': 'fetch',
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : undefined;

  if (!res.ok) {
    const err = data as { error?: { code?: string; message?: string } } | undefined;
    throw new ApiError(res.status, err?.error?.code ?? 'error', err?.error?.message ?? 'Feil');
  }
  return data as T;
}

export const api = {
  register: (email: string, password: string) =>
    request<{ ok: true }>('POST', '/auth/register', { email, password }),
  login: (email: string, password: string) =>
    request<{ ok: true }>('POST', '/auth/login', { email, password }),
  logout: () => request<{ ok: true }>('POST', '/auth/logout'),
  me: () => request<MeResponse>('GET', '/auth/me'),
  requestReset: (email: string) => request<{ ok: true }>('POST', '/auth/reset', { email }),
  confirmReset: (token: string, password: string) =>
    request<{ ok: true }>('POST', '/auth/reset/confirm', { token, password }),
  deleteAccount: (password: string) =>
    request<{ ok: true }>('DELETE', '/auth/account', { password }),
  checkout: (provider: PaymentProvider) =>
    request<CheckoutResponse>('POST', '/payments/checkout', { provider }),

  syncPush: (changes: unknown[]) => request<{ results: unknown[] }>('POST', '/sync', { changes }),
  syncPull: (since: number, limit: number) =>
    request<{ changes: unknown[]; nextSince: number; more: boolean }>(
      'GET',
      `/sync?since=${since}&limit=${limit}`,
    ),

  // Live trip sharing (spec 15) — used only by the sharing device. `credentials:
  // same-origin` still applies here (fine: the write endpoints authorize by
  // writeKey, not cookie; a cookie only lets create() record userId).
  shareCreate: (boatName: string | undefined, ttlHours: number | undefined) =>
    request<ShareCreateResponse>('POST', '/share', { boatName, ttlHours }),
  sharePoints: (id: string, writeKey: string, points: SharePointIn[]) =>
    request<{ ok: true }>('POST', `/share/${id}/points`, { writeKey, points }),
  shareEnd: (id: string, writeKey: string) =>
    request<{ ok: true }>('POST', `/share/${id}/end`, { writeKey }),
};

export { request as apiRequest };

/**
 * Public share read (spec 15 viewer) — deliberately NOT `api.request`: no
 * cookies, no CSRF header, so `/del/:id` never depends on being signed in and
 * a leaked link can't be used to infer anything about the viewer's session.
 */
export async function fetchShareView(id: string): Promise<ShareGetResponse | { status: 404 | 410 }> {
  const res = await fetch(`/api/share/${id}`, { credentials: 'omit' });
  if (res.status === 404 || res.status === 410) return { status: res.status };
  if (!res.ok) throw new ApiError(res.status, 'error', 'Feil');
  return (await res.json()) as ShareGetResponse;
}
